import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";

/**
 * Newsletter signup modal. Posts to POST /api/newsletter/subscribe which
 * upserts a Klaviyo profile and adds it to list `Rj8phF`. Modal chrome
 * mirrors `RSVPModal` and `InquireModal` for visual consistency, and the
 * "Subscribe" button reuses the site's gold-base / navy-sweep convention
 * via `.rsvp-sweep` so the action reads as part of the same family.
 */
export function NewsletterModal({ isOpen, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [serverError, setServerError] = useState("");
  const firstInputRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    setStatus("idle");
    setErrors({});
    setServerError("");
    const t = setTimeout(() => firstInputRef.current?.focus(), 100);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(t);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => () => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
  }, []);

  const update = (field) => (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Please enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    if (!validate()) return;
    setStatus("submitting");
    setServerError("");
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      if (!data?.success) throw new Error("subscribe failed");
      setStatus("success");
      setForm({ name: "", email: "", phone: "" });
      autoCloseTimerRef.current = setTimeout(() => onClose?.(), 4000);
    } catch (err) {
      const is429 = err?.response?.status === 429;
      setServerError(
        is429
          ? "Too many requests. Please try again in a few minutes."
          : "Something went wrong. Please try again or email us at parkerjr2@gmail.com."
      );
      setStatus("idle");
    }
  };

  const inputClass = "w-full border border-[#0A1128]/15 px-4 py-2.5 text-sm focus:border-[#0A1128] focus:ring-1 focus:ring-[#0A1128] outline-none transition-colors";
  const labelClass = "block text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-1.5";

  if (!isOpen) return null;

  const modal = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 bg-[#0A1128]/60 backdrop-blur-sm overflow-y-auto"
        style={{ zIndex: 9999 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-modal-title"
        data-testid="newsletter-modal-backdrop"
      >
        {/* Scroll wrapper: min-h-full guarantees the flex container is at
            least viewport-tall, so vertical centering works when content is
            short, and pushing-to-top + scroll happens when content is taller
            than the viewport. */}
        <div className="min-h-full flex items-center justify-center px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-[480px] relative"
            style={{ zIndex: 10000 }}
            data-testid="newsletter-modal"
          >
          <button
            onClick={onClose}
            aria-label="Close newsletter signup"
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-[#8A94A6] hover:text-[#0A1128] transition-colors"
            data-testid="newsletter-modal-close"
          >
            <X size={18} />
          </button>

          <div className="px-6 py-7 md:px-10 md:py-9">
            {status === "success" ? (
              <div className="text-center py-6" data-testid="newsletter-modal-success">
                <div className="w-12 h-[1px] bg-[#CBA365] mx-auto mb-6" />
                <h2
                  className="section-heading font-heading text-2xl sm:text-3xl text-[#0A1128] mb-3"
                  style={{ letterSpacing: "-0.01em", lineHeight: 1.1 }}
                >
                  You're on the list!
                </h2>
                <p className="text-sm text-[#4A5568] leading-relaxed">
                  Thank you for subscribing. We'll be in touch with the latest from Rain Boy Fine Art.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6 text-left">
                  <p className="overline mb-2" style={{ textAlign: "left" }}>Newsletter</p>
                  <h2
                    id="newsletter-modal-title"
                    className="section-heading font-heading text-2xl sm:text-3xl text-[#0A1128] mb-2 text-left"
                    style={{ letterSpacing: "-0.01em", lineHeight: 1.1 }}
                  >
                    Stay in the Loop
                  </h2>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "rgba(10, 25, 41, 0.6)", lineHeight: 1.5, textAlign: "left" }}>
                    Get updates on new work, upcoming shows, and exclusive events.
                  </p>
                </div>

                {serverError && (
                  <div
                    className="mb-5 p-4 bg-red-50 border border-red-200 text-red-700 text-sm"
                    role="alert"
                    aria-live="polite"
                    data-testid="newsletter-modal-error"
                  >
                    {serverError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" noValidate data-testid="newsletter-form">
                  <div>
                    <label htmlFor="newsletter-name" className={labelClass}>Name *</label>
                    <input
                      id="newsletter-name"
                      ref={firstInputRef}
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className={inputClass}
                      disabled={status === "submitting"}
                      autoComplete="name"
                      data-testid="newsletter-input-name"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="newsletter-email" className={labelClass}>Email *</label>
                    <input
                      id="newsletter-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="Your email address"
                      className={inputClass}
                      disabled={status === "submitting"}
                      autoComplete="email"
                      data-testid="newsletter-input-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="newsletter-phone" className={labelClass}>Phone</label>
                    <input
                      id="newsletter-phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Phone number (optional)"
                      className={inputClass}
                      disabled={status === "submitting"}
                      autoComplete="tel"
                      data-testid="newsletter-input-phone"
                    />
                  </div>

                  <p
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: "0.72rem",
                      lineHeight: 1.55,
                      color: "rgba(10, 25, 41, 0.55)",
                      marginTop: "0.75rem",
                    }}
                  >
                    By submitting this form, you agree that Rain Boy Fine Art may contact you via email, phone, or SMS about upcoming exhibitions, new work, and gallery news. You can unsubscribe at any time.
                  </p>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="rsvp-sweep relative overflow-hidden w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: "#CBA365",
                      color: "#0A1128",
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      letterSpacing: "0.32em",
                      textTransform: "uppercase",
                      padding: "0.95rem 2rem",
                      transition: "color 0.4s ease",
                    }}
                    data-testid="newsletter-submit"
                  >
                    <span aria-hidden="true" className="rsvp-sweep__fill absolute inset-0" style={{ backgroundColor: "#0A1128" }} />
                    <span className="relative z-10 flex items-center gap-2">
                      {status === "submitting" ? (
                        <><Loader2 size={14} className="animate-spin" /> Subscribing...</>
                      ) : (
                        "Subscribe"
                      )}
                    </span>
                  </button>
                </form>
              </>
            )}
          </div>
        </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
}
